import React, {
  memo,
  ReactNode,
} from 'react';
import {
  ChargesRowData,
  Claim,
} from 'pages/Dashboard/pages/Billing/types';
import isNil from 'lodash/isNil';
import compact from 'lodash/compact';
import {
  pluralize,
} from '@populateui/utils';
import map from 'lodash/map';
import ClaimRow from 'pages/Dashboard/pages/Billing/Charges/columns/ClaimRow';
import Tag from 'core/components/Tag';
import Skeleton from 'core/components/Skeleton';

type ServiceCodeInfoProps = {
  attributes: (string | ReactNode)[];
  name: string;
};

export function ServiceCodeInfo({
  attributes,
  name,
}: ServiceCodeInfoProps) {
  return (
    <div className="text-xs flex gap-2 w-full items-center">
      <strong className="font-semibold after:inline-block after:relative after:ml-2 after:content-['•'] last:after:!hidden">{name}</strong>
      <div className="flex items-center gap-2 w-full text-slate-600">
        {attributes.map((attribute) => (
          <div
            key={attribute as string}
            className="flex after:inline-block after:relative after:ml-2 after:content-['•'] last:after:!hidden"
          >
            {attribute}
          </div>
        ))}
      </div>
    </div>
  );
}

function _ClaimInfo(data: ChargesRowData) {
  const {
    claimIndex,
    patientId,
    secondaryClaim,
    isClaim,
  } = data;
  return isClaim ? (
    <div className="flex flex-col">
      <ClaimRow
        claim={data}
        claimIndex={claimIndex}
        patientId={patientId}
      />
      {!isNil(secondaryClaim) && (
        <ClaimRow
          claim={secondaryClaim as Claim}
          claimIndex={claimIndex}
          patientId={patientId}
        />
      )}
    </div>
  ) : null;
}

export const ClaimInfo = memo(_ClaimInfo);

function _CodeInfo({
  code,
  serviceName,
  numberOfUnits,
  modifiers,
  linkedICDCodes,
  isCode,
  visitNoteBillingCustomServiceCodeId,
}: ChargesRowData) {
  const isCustom = !isNil(visitNoteBillingCustomServiceCodeId);

  return isCode ? (
    <ServiceCodeInfo
      name={code ?? serviceName ?? ''}
      attributes={isCustom
        ? compact([
          (<Tag
            key="custom"
            tagName="Custom"
            className="!px-2 !py-0 w-fit"
            defaultColor="!bg-gray-200 !text-gray-700 !font-semibold"
          />),
          (numberOfUnits ?? 0) > 0
            ? pluralize('unit', numberOfUnits)
            : null,
        ])
        : compact([
          (modifiers ?? []).length > 0
            ? map(modifiers ?? [], 'code').join(', ')
            : null,
          (linkedICDCodes ?? []).length > 0
            ? map(linkedICDCodes ?? [], 'code').join(', ')
            : null,
          (numberOfUnits ?? 0) > 0
            ? pluralize('unit', numberOfUnits)
            : null,
        ])}
    />
  ) : null;
}

export const CodeInfo = memo(_CodeInfo);

function _ServiceCell(data: ChargesRowData) {
  return data.isDetailsLoading ? (
    <div className="flex w-full items-center py-1">
      <Skeleton variant="rectangular" className="!w-full !h-4 !rounded" />
    </div>
  ) : (
    <div className="flex flex-col gap-2 w-full !truncate">
      <ClaimInfo {...data} />
      <CodeInfo {...data} />
    </div>
  );
}

export default memo(_ServiceCell);
